import React, {CSSProperties, FC, ReactNode, useCallback} from "react";
import {Space, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {Panel, StatusTag} from "../../../Components/ui";
import type {StatusTone} from "../../../Components/ui";
import {humanizeEnumValue} from "../../../i18n/labelHelpers";
import {formatLocalDateTime} from "../adminDateFormat";
import {
    EMPTY_OPERATIONS_VALUE,
    operationalOfferStatusTone,
    operationalPaymentStatusTone
} from "../participantOperationsHelpers";
import {displayValue, isPresent} from "./participantDetailHelpers";
import type {ParticipantRegistration} from "./participantDetailTypes";

export const FIELD_GRID_STYLE: CSSProperties = {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))"
};

// One empty-state text for every list and the missing-participant state on this page.
export const useEmptyText = () => {
    const {t} = useTranslation();

    return t("participant-detail.empty", {defaultValue: "No data"});
};

export const useEnumLabel = () => {
    const {t} = useTranslation();

    return useCallback((value?: string | null) => value
        ? t(`enums:${value}`, {defaultValue: humanizeEnumValue(value, EMPTY_OPERATIONS_VALUE)})
        : EMPTY_OPERATIONS_VALUE, [t]);
};

export const EnumTag: FC<{value?: string | null; tone?: StatusTone}> = ({value, tone}) => {
    const enumLabel = useEnumLabel();

    return <StatusTag tone={tone}>{enumLabel(value)}</StatusTag>;
};

export const OfferSummary: FC<{
    offerNumber?: string | null;
    offerStatus?: string | null;
    paymentStatus?: string | null;
    paymentMethod?: string | null;
}> = ({offerNumber, offerStatus, paymentStatus, paymentMethod}) => {
    const enumLabel = useEnumLabel();

    if (![offerNumber, offerStatus, paymentStatus, paymentMethod].some(isPresent)) {
        return <>{EMPTY_OPERATIONS_VALUE}</>;
    }

    return (
        <Space direction="vertical" size={2}>
            <Typography.Text>{displayValue(offerNumber)}</Typography.Text>
            <Space size={4} wrap>
                {offerStatus ? <EnumTag value={offerStatus} tone={operationalOfferStatusTone(offerStatus)}/> : null}
                {paymentStatus ? <EnumTag value={paymentStatus} tone={operationalPaymentStatusTone(paymentStatus)}/> : null}
            </Space>
            {paymentMethod ? (
                <Typography.Text type="secondary">{enumLabel(paymentMethod)}</Typography.Text>
            ) : null}
        </Space>
    );
};

export const GdprConsentSummary: FC<{registration: ParticipantRegistration}> = ({registration}) => {
    const {t} = useTranslation();

    if (!registration.gdprConsentAccepted) {
        return (
            <StatusTag tone="neutral">
                {t("privacy-consent-missing", {defaultValue: "Missing"})}
            </StatusTag>
        );
    }

    return (
        <Space direction="vertical" size={0}>
            <StatusTag tone="success">
                {t("privacy-consent-recorded", {defaultValue: "Recorded"})}
            </StatusTag>
            {registration.gdprConsentAcceptedAtUtc ? (
                <Typography.Text type="secondary">
                    {formatLocalDateTime(registration.gdprConsentAcceptedAtUtc, EMPTY_OPERATIONS_VALUE)}
                </Typography.Text>
            ) : null}
            {registration.gdprConsentTextVersion ? (
                <Typography.Text type="secondary">
                    {t("privacy-consent-version", {defaultValue: "Version"})} {registration.gdprConsentTextVersion}
                </Typography.Text>
            ) : null}
            {registration.gdprConsentEventId ? (
                <Typography.Text type="secondary">
                    {t("privacy-consent-event-id", {defaultValue: "Consent event ID"})} {registration.gdprConsentEventId}
                </Typography.Text>
            ) : null}
        </Space>
    );
};

export const DetailPanel: FC<{
    title: ReactNode;
    description?: ReactNode;
    extra?: ReactNode;
    children: ReactNode;
}> = ({title, description, extra, children}) => (
    <Panel title={title} description={description} extra={extra}>
        <div className="ati-stack">{children}</div>
    </Panel>
);
